import {
  dateKey,
  DEFAULT_TIMEZONE,
  InteractionType,
  type CatalogItemDto,
  type NextStep,
  type TaskInteractionType,
} from '@performance-crm/shared';
import { defaultNextTask, fromLocalInput, TYPE_LABEL } from '@/lib/format';
import { emptyAppointment, toAppointmentDetails, type AppointmentForm } from './appointment';

export interface NextStepForm {
  kind: 'TASK' | 'DESIST';
  /** Solo tipos de tarea: una nota no es un próximo paso. */
  type: TaskInteractionType;
  scheduledAt: string;
  description: string;
  /** Modalidad, solo cuando la tarea es una cita. */
  appointment: AppointmentForm;
  reason: string;
  comment: string;
}

/** Formulario vacío; la tarea por defecto es mañana en horario de oficina de la zona de la empresa. */
export function emptyNextStep(tz?: string): NextStepForm {
  return {
    kind: 'TASK',
    type: InteractionType.LLAMADA,
    scheduledAt: defaultNextTask(tz),
    description: '',
    appointment: emptyAppointment(),
    reason: '',
    comment: '',
  };
}

export type NextTask = Extract<NextStep, { kind: 'TASK' }>;

/** La tarea programada del formulario (con la cita completa si es SE_CITO); también la usa «Reabrir lead». */
export function toNextTask(f: NextStepForm, tz?: string): NextTask {
  return {
    kind: 'TASK',
    type: f.type,
    scheduledAt: new Date(fromLocalInput(f.scheduledAt, tz)),
    description: f.description || null,
    ...(f.type === 'SE_CITO' ? { appointment: toAppointmentDetails(f.appointment) } : {}),
  };
}

export function toNextStep(f: NextStepForm, tz?: string): NextStep {
  if (f.kind === 'DESIST') return { kind: 'DESIST', reason: f.reason, comment: f.comment || null };
  return toNextTask(f, tz);
}

/** Formateadores por zona horaria (uno por zona: la de la empresa activa, Lima por defecto). */
const formatters = new Map<
  string,
  { timeOfDay: Intl.DateTimeFormat; dayLabel: Intl.DateTimeFormat }
>();
function formattersFor(tz: string) {
  let f = formatters.get(tz);
  if (!f) {
    f = {
      timeOfDay: new Intl.DateTimeFormat('es-PE', {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
      dayLabel: new Intl.DateTimeFormat('es-PE', {
        timeZone: tz,
        weekday: 'short',
        day: '2-digit',
        month: 'short',
      }),
    };
    formatters.set(tz, f);
  }
  return f;
}

/** «hoy», «mañana» o «vie 24 oct», según el día (zona de la empresa) de la fecha elegida. */
export function relativeDay(date: Date, now = new Date(), tz: string = DEFAULT_TIMEZONE): string {
  const day = dateKey(date, tz);
  if (day === dateKey(now, tz)) return 'hoy';
  if (day === dateKey(new Date(now.getTime() + 24 * 3_600_000), tz)) return 'mañana';
  return formattersFor(tz).dayLabel.format(date);
}

/**
 * Resumen de lo que va a pasar al guardar (ux-mi-dia-16): el siguiente paso viene prellenado (llamada mañana a la
 * misma hora) y conviene que la decisión sea consciente. «Se programará: Llamada · mañana 10:00 a. m.» o
 * «Se desistirá el lead · No contesta».
 */
export function describeNextStep(
  f: NextStepForm,
  desistReasons: CatalogItemDto[] = [],
  now = new Date(),
  tz: string = DEFAULT_TIMEZONE,
): string {
  if (f.kind === 'DESIST') {
    const reason = desistReasons.find((r) => r.code === f.reason)?.label;
    return reason ? `Se desistirá el lead · ${reason}` : 'Se desistirá el lead · elige el motivo';
  }
  const at = f.scheduledAt ? new Date(fromLocalInput(f.scheduledAt, tz)) : null;
  const when =
    at && !Number.isNaN(at.getTime())
      ? `${relativeDay(at, now, tz)} ${formattersFor(tz).timeOfDay.format(at)}`
      : 'elige fecha y hora';
  return `Se programará: ${TYPE_LABEL[f.type]} · ${when}`;
}

/** Quién atiende la cita: el asesor de sala elegido o, si no hay, el del lead. El aviso de disponibilidad mira a esa persona. */
export function effectiveAppointmentAdvisor(
  appointment: { roomAdvisorId: string },
  leadAdvisorId: string | null | undefined,
) {
  return appointment.roomAdvisorId || leadAdvisorId || null;
}
